/**
 * ScientificFormulasPage — Public scientific specification page.
 *
 * Comprehensive reference for every index, score, model and biomarker
 * used in Nutramilo, with peer-reviewed citations (PMID/DOI). Designed
 * for clinicians, patients and journalists asking "is this credible?".
 *
 * Route: /scientific-formulas
 * Linked from: LandingFooter (footer-link-formulas)
 *
 * Bilingual (BG primary, EN secondary). Clean Bulgarian, no transliteration.
 */
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageFooter } from "@/components/SubpageLayout";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Search, BookOpen, ExternalLink, Calculator, FlaskRound, HeartPulse, Brain, Activity, Dna, ScanLine, Bot } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// Formula catalogue — every index/model with formula + citation.
// Grouped by domain for skimmability.
// ─────────────────────────────────────────────────────────────────────
const CATALOGUE = [
  {
    group: { bg: "Хранене — Инсулинов отговор", en: "Nutrition — Insulin response" },
    icon: FlaskRound,
    accent: "#5e227f",
    items: [
      {
        name: "DIL (Dietary Insulin Load) — Инсулинов товар",
        bg: "Инсулинов товар на ниво храна — измерва общото инсулиногенно натоварване от ястието.",
        en: "Insulin load per food — measures total insulinogenic burden of the meal.",
        formula: "DIL_per_100g = 0.69 · Protein_g + max(0, Carbs_g − Fiber_g)",
        refs: [
          { label: "Holt 1997, AJCN 66(5):1264", url: "https://pubmed.ncbi.nlm.nih.gov/9356547" },
          { label: "Bell 2015, AJCN — Validation of the food insulin index", url: "https://pubmed.ncbi.nlm.nih.gov/26354547" },
          { label: "Bao 2011, AJCN — Postprandial glycemia & insulinemia (food insulin index)", url: "https://pubmed.ncbi.nlm.nih.gov/21325437" },
        ],
      },
      {
        name: "NIS v1.1.9 (Nutramilo Insulin Score) — Инсулинов рейтинг",
        bg: "Нормализиран 0–100% инсулинов рейтинг на ястието. Категории: Нисък/Умерен/Среден/Висок/Много висок. Frozen калибрация v1.1.9.",
        en: "Normalised 0–100% insulin rating of the meal. Tiers: Low/Moderate/Medium/High/Very high. Frozen v1.1.9 calibration.",
        formula:
          "raw_NIS = (1.61·C + 0.66·P + 1.20·F − 1.14·Fi) / Energy_kJ × 1000\n" +
          "NIS = clamp(0..100, 0.7275 · raw_NIS + 8.111)\n" +
          "Energy_kJ = (P·4 + C·4 + F·9) · 4.184",
        refs: [
          { label: "Holt Insulin Index 1997, AJCN 66:1264", url: "https://pubmed.ncbi.nlm.nih.gov/9356547" },
          { label: "Bell 2015, AJCN — Validation of the food insulin index", url: "https://pubmed.ncbi.nlm.nih.gov/26354547" },
        ],
      },
      {
        name: "Glycemic Load (GL)",
        bg: "Гликемичен товар = GI × налични въглехидрати / 100.",
        en: "Glycemic Load = GI × available carbs / 100.",
        formula: "GL = (GI_food × (Carbs_g − Fiber_g)) / 100",
        refs: [{ label: "Foster-Powell 2002, AJCN 76(1):5", url: "https://pubmed.ncbi.nlm.nih.gov/12081815" }],
      },
      {
        name: "Personalised Daily DIL Budget — Дневен инсулинов бюджет",
        bg: "Дневен инсулинов бюджет — твоят персонален лимит на инсулиновия товар за деня, адаптиран спрямо тегло, HOMA-IR и физическа активност.",
        en: "Daily Insulin Budget — your personal cap on daily insulin load, adjusted for weight, HOMA-IR, activity.",
        formula:
          "base_budget = 90 (M) / 75 (F)\n" +
          "HOMA-IR ≥ 2.5 → ×0.8 ; ≥ 5.0 → ×0.65\n" +
          "activity ≥ 8000 steps/day → ×1.15",
        refs: [{ label: "Bao 2011, AJCN — Predicting postprandial insulinemia from food insulin index", url: "https://pubmed.ncbi.nlm.nih.gov/21325437" }],
      },
    ],
  },

  {
    group: { bg: "Хранене — Качество и плътност", en: "Nutrition — Quality & density" },
    icon: Calculator,
    accent: "#9c39a1",
    items: [
      {
        name: "Calorie Density (Volumetrics)",
        bg: "kcal/g. Препоръчителен диапазон ≤ 1.5 kcal/g за обем без преяждане.",
        en: "kcal/g. Target ≤ 1.5 kcal/g for volume without overeating.",
        formula: "calorie_density = kcal / weight_g",
        refs: [{ label: "Ello-Martin 2005, AJCN — Energy density & weight management", url: "https://pubmed.ncbi.nlm.nih.gov/16002828" }],
      },
      {
        name: "Nutrient Density (NRF9.3 / NRF11.3)",
        bg: "Nutrient-Rich Foods Index — съотношение полезни/вредни нутриенти за 100 kcal.",
        en: "Nutrient-Rich Foods Index — beneficial/limit nutrients per 100 kcal.",
        formula: "NRF = Σ (positive_nutrients / DRV × 100) − Σ (limit_nutrients / LIM × 100)",
        refs: [{ label: "Drewnowski 2009, J Am Coll Nutr — Nutrient Rich Foods Index", url: "https://pubmed.ncbi.nlm.nih.gov/20368382" }],
      },
      {
        name: "Nutri-Score (A–E)",
        bg: "Официалният EU FOP етикет — Франция, Белгия, Германия, Испания, Нидерландия, Швейцария.",
        en: "Official EU FOP label — FR, BE, DE, ES, NL, CH.",
        formula: "Score = Σ negative_points (energy + sat fat + sugars + sodium) − Σ positive_points (fiber + protein + fruit/veg/nuts)",
        refs: [{ label: "Santé publique France 2016 · EU 2020/C 286/04", url: "https://www.santepubliquefrance.fr/en/nutri-score" }],
      },
      {
        name: "Satiety Index",
        bg: "Ситост 0–100 на база протеин, фибри, енергийна плътност и палатабилност.",
        en: "Satiety 0–100 from protein, fiber, energy density and palatability.",
        formula: "satiety_score = w1·protein_density + w2·fiber_density − w3·energy_density + w4·water%",
        refs: [{ label: "Holt 1995, EJCN 49(9):675", url: "https://pubmed.ncbi.nlm.nih.gov/7498104" }],
      },
      {
        name: "PRAL (Potential Renal Acid Load)",
        bg: "Алкален/кисел потенциал. Важен за бъбреци, камъни, остеопороза.",
        en: "Alkaline/acid load. Relevant for kidneys, stones, osteoporosis.",
        formula: "PRAL = 0.49·P + 0.037·Ph + 0.027·SO4 − 0.021·K − 0.026·Mg − 0.013·Ca",
        refs: [{ label: "Remer & Manz 1995, J Am Diet Assoc 95(7):791", url: "https://pubmed.ncbi.nlm.nih.gov/7797810" }],
      },
      {
        name: "NOVA classification",
        bg: "1 (непреработена) → 4 (ултра-преработена). WHO/PAHO одобрено.",
        en: "1 (unprocessed) → 4 (ultra-processed). WHO/PAHO endorsed.",
        formula: "NOVA ∈ {1, 2, 3, 4} — heuristic on ingredient list",
        refs: [{ label: "Monteiro 2018, Public Health Nutr — NOVA & ultra-processing", url: "https://pubmed.ncbi.nlm.nih.gov/28322183" }],
      },
      {
        name: "FODMAP level",
        bg: "Ниво ферментируеми въглехидрати — нисък/умерен/висок. Важен за IBS/SIBO.",
        en: "Fermentable carbohydrate level — low/moderate/high. Key for IBS/SIBO.",
        formula: "lookup → Monash University FODMAP DB",
        refs: [{ label: "Monash University FODMAP DB", url: "https://www.monashfodmap.com" }],
      },
      {
        name: "Omega-6 : Omega-3 ratio",
        bg: "Противовъзпалителен баланс. Оптимално ≤ 4:1, западна диета ~ 15–20:1.",
        en: "Anti-inflammatory balance. Target ≤ 4:1, Western diet 15–20:1.",
        formula: "ω6/ω3 = (LA + AA) / (ALA + EPA + DHA)",
        refs: [{ label: "Simopoulos 2016, Nutrients 8(3):128", url: "https://pubmed.ncbi.nlm.nih.gov/26950145" }],
      },
    ],
  },

  {
    group: { bg: "Възпаление и оксидативен стрес", en: "Inflammation & oxidative stress" },
    icon: HeartPulse,
    accent: "#e44288",
    items: [
      {
        name: "DII (Dietary Inflammatory Index)",
        bg: "Композитен индекс за противо-/проинфламаторни сигнали от 45 хранителни параметъра.",
        en: "Composite index across 45 dietary parameters.",
        formula: "DII = Σ (z_i − global_mean_i) · inflammatory_weight_i",
        refs: [{ label: "Shivappa 2014, Public Health Nutr — Designing the DII", url: "https://pubmed.ncbi.nlm.nih.gov/23941862" }],
      },
      {
        name: "EDIP (Empirical Dietary Inflammatory Pattern)",
        bg: "Емпиричен възпалителен модел — корелиран с CRP, IL-6, TNF-α.",
        en: "Empirical inflammatory pattern — correlated to CRP, IL-6, TNF-α.",
        formula: "EDIP = Σ food_group_intake · CRP_correlation_weight",
        refs: [{ label: "Tabung 2016, J Nutr 146(8):1560", url: "https://pubmed.ncbi.nlm.nih.gov/27358416" }],
      },
      {
        name: "NEAP (Net Endogenous Acid Production)",
        bg: "Естимат за мрежовия киселинен товар на дневното меню.",
        en: "Estimate of daily net acid production.",
        formula: "NEAP = 54.5 · (Protein / Potassium) − 10.2",
        refs: [{ label: "Frassetto 1998, AJCN 68(3):576", url: "https://pubmed.ncbi.nlm.nih.gov/9734733" }],
      },
      {
        name: "UIS / Inflammation Index (Nutramilo)",
        bg: "Композит EDIP + DII + NEAP — pro- срещу anti-inflammatory сигнали в храната.",
        en: "Composite EDIP + DII + NEAP — pro vs anti-inflammatory signals.",
        formula: "UIS = w1·EDIP + w2·DII + w3·NEAP (normalised 0–100, lower better)",
        refs: [
          { label: "Tabung 2016, J Nutr 146(8):1560", url: "https://pubmed.ncbi.nlm.nih.gov/27358416" },
          { label: "Shivappa 2014, Public Health Nutr — Designing the DII", url: "https://pubmed.ncbi.nlm.nih.gov/23941862" },
        ],
      },
    ],
  },

  {
    group: { bg: "Тяло и метаболизъм", en: "Body & metabolism" },
    icon: Activity,
    accent: "#287a2e",
    items: [
      {
        name: "BMI (Body Mass Index)",
        bg: "Индекс на телесна маса. Норма 18.5–24.9 kg/m².",
        en: "Body mass index. Normal 18.5–24.9 kg/m².",
        formula: "BMI = weight_kg / (height_m)²",
        refs: [{ label: "WHO Technical Report 854 (1995)", url: "https://www.who.int/europe/news-room/fact-sheets/item/a-healthy-lifestyle---who-recommendations" }],
      },
      {
        name: "WHR (Waist-to-Hip Ratio)",
        bg: "Талия/ханш. Висок риск: M > 0.90, F > 0.85.",
        en: "Waist/hip. High risk: M > 0.90, F > 0.85.",
        formula: "WHR = waist_cm / hip_cm",
        refs: [{ label: "WHO 2008 — Waist Circumference and WHR", url: "https://www.who.int/publications/i/item/9789241501491" }],
      },
      {
        name: "WHtR (Waist-to-Height Ratio)",
        bg: "Талия/ръст. Граница: > 0.50 — повишен метаболитен риск.",
        en: "Waist/height. Cut-off: > 0.50 — elevated metabolic risk.",
        formula: "WHtR = waist_cm / height_cm",
        refs: [{ label: "Ashwell 2012, Obes Rev 13(3):275", url: "https://pubmed.ncbi.nlm.nih.gov/22106927" }],
      },
      {
        name: "HOMA-IR (Insulin Resistance)",
        bg: "Маркер за инсулинова резистентност. Норма < 2.5.",
        en: "Insulin resistance marker. Normal < 2.5.",
        formula: "HOMA-IR = (Fasting_glucose_mmol × Fasting_insulin_µU) / 22.5",
        refs: [{ label: "Matthews 1985, Diabetologia 28(7):412", url: "https://pubmed.ncbi.nlm.nih.gov/3899825" }],
      },
      {
        name: "BMR (Basal Metabolic Rate)",
        bg: "Базова метаболитна скорост — Mifflin-St Jeor (валидиран срещу indirect calorimetry).",
        en: "Basal metabolic rate — Mifflin-St Jeor (validated vs indirect calorimetry).",
        formula:
          "M: BMR = 10·kg + 6.25·cm − 5·age + 5\n" +
          "F: BMR = 10·kg + 6.25·cm − 5·age − 161",
        refs: [{ label: "Mifflin 1990, AJCN 51(2):241", url: "https://pubmed.ncbi.nlm.nih.gov/2305711" }],
      },
      {
        name: "TDEE (Total Daily Energy Expenditure)",
        bg: "Общ дневен енергиен разход = BMR × activity factor.",
        en: "Total daily energy expenditure = BMR × activity factor.",
        formula:
          "TDEE = BMR × {sedentary 1.2 | light 1.375 | moderate 1.55 | active 1.725 | very active 1.9}",
        refs: [{ label: "FAO/WHO/UNU 2004 Expert Consultation", url: "https://www.fao.org/4/y5686e/y5686e.pdf" }],
      },
      {
        name: "VAIS (Visceral Adiposity Index Score)",
        bg: "Висцерален индекс на мазнините — оценка без MRI.",
        en: "Visceral adiposity estimate without MRI.",
        formula:
          "M: VAI = (WC / (39.68 + 1.88·BMI)) · (TG/1.03) · (1.31/HDL)\n" +
          "F: VAI = (WC / (36.58 + 1.89·BMI)) · (TG/0.81) · (1.52/HDL)",
        refs: [{ label: "Amato 2010, Diabetes Care 33(4):920", url: "https://pubmed.ncbi.nlm.nih.gov/20067971" }],
      },
      {
        name: "eGFR (CKD-EPI 2021)",
        bg: "Бъбречна филтрация. CKD-EPI 2021 (race-free). Норма ≥ 90.",
        en: "Renal filtration. CKD-EPI 2021 race-free. Normal ≥ 90.",
        formula: "eGFR = 142 · min(Scr/κ,1)^α · max(Scr/κ,1)^−1.200 · 0.9938^age · (1.012 if F)",
        refs: [{ label: "Inker 2021, NEJM 385(19):1737", url: "https://pubmed.ncbi.nlm.nih.gov/34554658" }],
      },
    ],
  },

  {
    group: { bg: "Стареене и епигенетика", en: "Ageing & epigenetics" },
    icon: Dna,
    accent: "#0e5c60",
    items: [
      {
        name: "PhenoAge (Levine 2018)",
        bg: "Биологична възраст от 9 биомаркера. Validated vs all-cause mortality.",
        en: "Biological age from 9 biomarkers. Validated vs all-cause mortality.",
        formula:
          "xb = Σ (β_i · marker_i) − 19.907 (9 markers: albumin, creat, glucose, CRP, lymph%, MCV, RDW, ALP, WBC, age)\n" +
          "M = 1 − exp(−0.0076927·exp(xb))\n" +
          "PhenoAge = 141.50 + ln(−0.00553·ln(1−M)) / 0.090165",
        refs: [{ label: "Levine 2018, Aging 10(4):573", url: "https://pubmed.ncbi.nlm.nih.gov/29676998" }],
      },
      {
        name: "DunedinPACE",
        bg: "Темп на биологично стареене (Pace of Aging). По-нисък = по-бавно стареене.",
        en: "Pace of biological ageing. Lower = slower ageing.",
        formula: "DunedinPACE = function of 173 CpG methylation sites (epigenetic clock)",
        refs: [{ label: "Belsky 2022, eLife 11:e73420", url: "https://pubmed.ncbi.nlm.nih.gov/35029144" }],
      },
      {
        name: "Nutramilo Epigenetic Map (модул)",
        bg: "Интерактивна Sankey диаграма на епигенетичните пътища при 340+ заболявания (вкл. редки синдроми). Disease Discovery mode с live търсене през всички pathways. Свързва заболяване → епигенетични пътища → регулиращи гени → хранителни и lifestyle модулатори. Базиран на KEGG, Open Targets и ClinVar.",
        en: "Interactive Sankey diagram of epigenetic pathways across 340+ diseases (incl. rare syndromes). Disease Discovery mode with live cross-pathway search. Links disease → epigenetic pathways → regulator genes → dietary and lifestyle modulators. Based on KEGG, Open Targets and ClinVar.",
        formula: "disease → {epigenetic_pathways[]} → {regulator_genes[]} → {food_modulators[], lifestyle_modulators[]}\n" +
                 "Sankey flow weights derived from gene–disease association scores (Open Targets) + KEGG pathway membership.",
        refs: [
          { label: "Kanehisa 2017 — KEGG", url: "https://pubmed.ncbi.nlm.nih.gov/27899662" },
          { label: "Ochoa 2021 — Open Targets", url: "https://pubmed.ncbi.nlm.nih.gov/33196847" },
          { label: "Landrum 2020 — ClinVar", url: "https://pubmed.ncbi.nlm.nih.gov/31777943" },
        ],
      },
    ],
  },

  {
    group: { bg: "Гинекология и хормони", en: "Gynaecology & hormones" },
    icon: HeartPulse,
    accent: "#B46B5C",
    items: [
      {
        name: "2-OHE1 / 16α-OHE1 Ratio (estrogen metabolism)",
        bg: "Съотношение защитен спрямо пролиферативен метаболит на естрогена. Норма ≥ 0.7.",
        en: "Ratio of protective vs proliferative estrogen metabolite. Normal ≥ 0.7.",
        formula: "ratio_2_16 = 2-OHE1 / 16α-OHE1 (от 24h урина или урина с креатинин)",
        refs: [
          { label: "Bradlow 1996, J Endocrinol — 2-hydroxyestrone: the 'good' estrogen", url: "https://pubmed.ncbi.nlm.nih.gov/8943806" },
          { label: "Michnovicz 1997, J Natl Cancer Inst — I3C & urinary estrogen metabolites", url: "https://pubmed.ncbi.nlm.nih.gov/9168187" },
        ],
      },
      {
        name: "Methylation Activity (estrogen)",
        bg: "Активност на метилиране — 2-MeOE1 / 2-OHE1. Норма ≥ 0.15.",
        en: "Methylation activity — 2-MeOE1 / 2-OHE1. Normal ≥ 0.15.",
        formula: "methylation_activity = 2-MeOE1 / 2-OHE1",
        refs: [{ label: "Ziegler 2015, Steroids — Estrogen metabolism & breast cancer", url: "https://pubmed.ncbi.nlm.nih.gov/25725255" }],
      },
      {
        name: "4-OHE1 burden",
        bg: "Натоварване с генотоксичен 4-хидрокси-естрон. Норма < 4.75 µg/g креатинин.",
        en: "Genotoxic 4-hydroxy-estrone load. Normal < 4.75 µg/g creatinine.",
        formula: "4OH_burden = 4-OHE1 / creatinine_g (urine)",
        refs: [{ label: "Cavalieri 2014, Mol Aspects Med — Molecular etiology of estrogen-initiated cancers", url: "https://pubmed.ncbi.nlm.nih.gov/23994691" }],
      },
      {
        name: " (Breast Cancer Risk, IBIS)",
        bg: "10-годишен и lifetime риск от рак на гърдата. Стандарт за висок риск (≥ 20%).",
        en: "10-year and lifetime breast cancer risk. Standard for high-risk (≥ 20%).",
        formula: "P(BC | age, family, BMI, menarche, parity, BRCA, biopsy, density)",
        refs: [{ label: "Tyrer 2004, Stat Med 23(7):1111", url: "https://pubmed.ncbi.nlm.nih.gov/15057881" }],
      },
      {
        name: "Breast Density (BI-RADS A–D)",
        bg: "Плътност на гърдата — софтуер за образна диагностика (мамография).",
        en: "Breast density — imaging diagnostic software (mammography).",
        formula: "BI-RADS classification on mammographic image (A almost-fatty → D extremely dense)",
        refs: [{ label: "ACR BI-RADS 5th Edition 2013", url: "https://www.acr.org/Clinical-Resources/Reporting-and-Data-Systems/Bi-Rads" }],
      },
      {
        name: "COL3 (Collagen Synthesis Score)",
        bg: "Собствен скор за подкрепа на синтез на колаген тип III (Vit C, медь, амино киселини).",
        en: "Our own score for type-III collagen synthesis support (Vit C, copper, amino acids).",
        formula: "col3 = w1·vit_C + w2·copper + w3·glycine + w4·proline + w5·lysine (normalised 0–100)",
        refs: [{ label: "DePhillipo 2018, Orthop J Sports Med — Vitamin C & collagen synthesis (review)", url: "https://pubmed.ncbi.nlm.nih.gov/30386805" }],
      },
    ],
  },

  {
    group: { bg: "Кардио-метаболитни рискове", en: "Cardio-metabolic risks" },
    icon: HeartPulse,
    accent: "#5e227f",
    items: [
      {
        name: "Framingham Risk Score",
        bg: "10-годишен ССЗ риск. Стандарт за първична профилактика.",
        en: "10-year CVD risk. Primary-prevention standard.",
        formula: "logit(P) = β1·age + β2·SBP + β3·smoking + β4·diabetes + β5·TC + β6·HDL",
        refs: [{ label: "D'Agostino 2008, Circulation 117(6):743", url: "https://pubmed.ncbi.nlm.nih.gov/18212285" }],
      },
      {
        name: "FINDRISC (Diabetes 10y risk)",
        bg: "Финландски риск-калкулатор за T2D. Норма < 7 точки.",
        en: "Finnish T2D risk calculator. Normal < 7.",
        formula: "score = age_pts + BMI_pts + WC_pts + activity_pts + diet_pts + meds_pts + glucose_pts + family_pts",
        refs: [{ label: "Lindström 2003, Diabetes Care 26(3):725", url: "https://pubmed.ncbi.nlm.nih.gov/12610029" }],
      },
    ],
  },

  {
    group: { bg: "Стоматология", en: "Dentistry" },
    icon: ScanLine,
    accent: "#0e5c60",
    items: [
      {
        name: "CAMBRA (Caries Management By Risk Assessment)",
        bg: "Управление на кариеса по риска. Стандарт на ADA.",
        en: "Caries Management By Risk Assessment. ADA standard.",
        formula: "risk = Σ disease_indicators − Σ protective_factors + Σ risk_factors",
        refs: [{ label: "Featherstone 2003, J Calif Dent Assoc — Caries balance contributing factors", url: "https://pubmed.ncbi.nlm.nih.gov/12636316" }],
      },
      {
        name: "EFP 2018 (Periodontitis classification)",
        bg: "Класификация на пародонтита по тежест (I–IV) и progression (A–C).",
        en: "Periodontitis staging (I–IV) and grading (A–C).",
        formula: "stage = max(CAL, RBL, tooth_loss); grade = bone_loss% / age",
        refs: [{ label: "Tonetti 2018, J Clin Periodontol 45 Suppl 20:S149", url: "https://pubmed.ncbi.nlm.nih.gov/29926495" }],
      },
    ],
  },

  {
    group: { bg: "Хранителни модели и пътища", en: "Dietary patterns & pathways" },
    icon: BookOpen,
    accent: "#287a2e",
    items: [
      {
        name: "Mediterranean Diet (MEDI-LITE)",
        bg: "Средиземноморска диета. PREDIMED: −30% риск от ССЗ при диабет.",
        en: "Mediterranean diet. PREDIMED: −30% CVD risk in diabetics.",
        formula: "MEDI-LITE = Σ food_group_serving_score (0–18 scale)",
        refs: [
          { label: "Sofi 2014, Public Health Nutr — Mediterranean diet meta-analysis & adherence score", url: "https://pubmed.ncbi.nlm.nih.gov/24476641" },
          { label: "Estruch 2013, NEJM 368(14):1279", url: "https://pubmed.ncbi.nlm.nih.gov/23432189" },
        ],
      },
      {
        name: "DASH score",
        bg: "Dietary Approaches to Stop Hypertension. Понижава SBP с 8–14 mmHg.",
        en: "DASH. Lowers SBP by 8–14 mmHg.",
        formula: "score = Σ quintile_pts (high: F&V, dairy, whole grains; low: sodium, red meat)",
        refs: [{ label: "Appel 1997, NEJM 336(16):1117", url: "https://pubmed.ncbi.nlm.nih.gov/9099655" }],
      },
      {
        name: "MIND score",
        bg: "Mediterranean-DASH for Neurodegenerative Delay. −53% Алцхаймер риск.",
        en: "MIND diet. −53% Alzheimer's risk.",
        formula: "score = Σ MIND_food_pts (15 components: berries, leafy greens, nuts, fish, olive oil, …)",
        refs: [{ label: "Morris 2015, Alzheimers Dement 11(9):1007", url: "https://pubmed.ncbi.nlm.nih.gov/26086182" }],
      },
      {
        name: "hPDI (Healthful Plant-based Diet Index)",
        bg: "Растителна диета (здравословна форма). −20% T2D риск.",
        en: "Healthful plant-based diet. −20% T2D risk.",
        formula: "score = Σ quintile (healthy plant foods) − Σ quintile (unhealthy plant + animal foods)",
        refs: [{ label: "Satija 2017, JACC 70(4):411", url: "https://pubmed.ncbi.nlm.nih.gov/28728684" }],
      },
      {
        name: "KEGG Metabolic Pathways",
        bg: "Над 500 пътища от Kyoto Encyclopedia of Genes and Genomes — използвани за map condition → biological pathway → food modulator.",
        en: "500+ pathways from KEGG — mapping condition → biological pathway → food modulator.",
        formula: "lookup: condition_id → pathway_id (KEGG) → food_modulator (peer-reviewed)",
        refs: [{ label: "Kanehisa 2017, NAR 45(D1):D353", url: "https://pubmed.ncbi.nlm.nih.gov/27899662" }],
      },
    ],
  },

  {
    group: { bg: "AI Агенти (RAG + Multi-Bot)", en: "AI Agents (RAG + Multi-Bot)" },
    icon: Bot,
    accent: "#9c39a1",
    items: [
      {
        name: "EndoBot",
        bg: "Ендокринна интерпретация — инсулин, кортизол, тироид, полови хормони. Vector store: 100+ ендокринни наръчника.",
        en: "Endocrine interpretation — insulin, cortisol, thyroid, sex hormones. Vector store: 100+ endocrine handbooks.",
        formula: "OpenAI Responses API + file_search (vector_store_id) → Bulgarian-grounded answer with PMID citations",
        refs: [{ label: "OpenAI Responses API + RAG", url: "https://platform.openai.com/docs/api-reference/responses" }],
      },
      {
        name: "NutriBot",
        bg: "Нутригеномика, хранителни взаимодействия, микронутриентен анализ.",
        en: "Nutrigenomics, food interactions, micronutrient analysis.",
        formula: "OpenAI Responses API + file_search (vs_69d6c8f5506c81918637cdfe7c9cf4c0) — Bulgarian, no transliteration",
        refs: [{ label: "RAG with peer-reviewed knowledge base", url: "https://platform.openai.com/docs/api-reference/responses" }],
      },
      {
        name: "NephroBot",
        bg: "Бъбречен товар, електролити, безопасност при ХБН.",
        en: "Renal load, electrolytes, CKD safety.",
        formula: "OpenAI Responses API + file_search (nephro vector store)",
        refs: [{ label: "KDIGO 2024 guidelines vector store", url: "https://kdigo.org/guidelines" }],
      },
      {
        name: "HemoBot",
        bg: "Анемии, желязо, B12, фолат, метилация.",
        en: "Anaemias, iron, B12, folate, methylation.",
        formula: "OpenAI Responses API + file_search (haematology vector store)",
        refs: [{ label: "ASH 2023 guidelines + Levine methylation papers", url: "https://www.hematology.org/education/clinicians/guidelines-and-quality-care" }],
      },
      {
        name: "LactoBot",
        bg: "Кърмене, лактация, пост-партум хранене.",
        en: "Breastfeeding, lactation, post-partum nutrition.",
        formula: "OpenAI Responses API + file_search (lactation vector store)",
        refs: [{ label: "WHO/UNICEF Baby-Friendly Hospital Initiative", url: "https://www.who.int/teams/nutrition-and-food-safety/food-and-nutrition-actions-in-health-systems/ten-steps-to-successful-breastfeeding" }],
      },
      {
        name: "GPT-5.2 (Menu generation)",
        bg: "AI агент за 7-дневно меню от готвени ястия, базирано на избрани от потребителя храни.",
        en: "AI agent for 7-day menu of cooked dishes, built only from user-picked foods.",
        formula: "OpenAI gpt-5.2 via emergentintegrations.LlmChat — async job pattern (start + poll/status)",
        refs: [{ label: "OpenAI gpt-5.2 + emergentintegrations", url: "https://platform.openai.com/docs/models" }],
      },
    ],
  },

  {
    group: { bg: "Образна диагностика (AI скрининги)", en: "Imaging (AI screenings)" },
    icon: ScanLine,
    accent: "#5e227f",
    items: [
      {
        name: "Mammography / Ultrasound / CT / MRI / X-ray",
        bg: "Образни модалности за анализ от лекар (ехография, мамография, CT-скенер, ЯМР, рентген). Nutramilo предоставя AI-подпомогнато визуално разчитане с задължителен лекарски валидиращ преглед.",
        en: "Imaging modalities (ultrasound, mammography, CT, MRI, X-ray). Nutramilo provides AI-assisted visual reading with mandatory clinician validation.",
        formula: "OpenAI Vision (gpt-4o-mini) → structured findings → clinician review",
        refs: [{ label: "ACR Practice Parameters for Breast Imaging", url: "https://www.acr.org/Clinical-Resources/Practice-Parameters-and-Technical-Standards" }],
      },
      {
        name: "Skin Screening (Dermatology)",
        bg: "Скрининг за злокачествени промени по кожата — Dermatology AI module. НЕ е диагноза.",
        en: "Skin malignancy screening — dermatology AI module. Not a diagnosis.",
        formula: "ABCDE + asymmetry/border/colour/diameter/evolution heuristic on AI vision output",
        refs: [{ label: "Esteva 2017, Nature 542:115", url: "https://pubmed.ncbi.nlm.nih.gov/28117445" }],
      },
    ],
  },

  {
    group: { bg: "Food Match Score (FMS)", en: "Food Match Score (FMS)" },
    icon: Calculator,
    accent: "#e44288",
    items: [
      {
        name: "Food Match Score (FMS)",
        bg: "Собствен композитен скор 0–100, който оценява всяка храна спрямо избраните състояния, фенотипи, диети, индекси и нутриенти. Това НЕ е патент — а собствен (peer-review pending) алгоритъм.",
        en: "Our own composite 0–100 score that ranks each food vs the selected conditions, phenotypes, diets, indices and nutrients. NOT a patent — our own (peer-review pending) algorithm.",
        formula:
          "FMS = 100 · (w1·disease_compat + w2·phenotype_compat + w3·diet_fit + w4·indices_match + w5·nutrient_dense)\n" +
          "weights: 0.35 / 0.20 / 0.15 / 0.20 / 0.10  (sum = 1.0)\n" +
          "+ cooking_bonus (±8 for cooked vs raw)",
        refs: [
          { label: "DIL — Holt 1997, AJCN 66(5):1264", url: "https://pubmed.ncbi.nlm.nih.gov/9356547" },
          { label: "NRF — Drewnowski 2009, J Am Coll Nutr", url: "https://pubmed.ncbi.nlm.nih.gov/20368382" },
          { label: "DII — Shivappa 2014, Public Health Nutr", url: "https://pubmed.ncbi.nlm.nih.gov/23941862" },
          { label: "EDIP — Tabung 2016", url: "https://pubmed.ncbi.nlm.nih.gov/27358416" },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────
export default function ScientificFormulasPage() {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const bg = language === "bg";
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return CATALOGUE;
    const q = query.toLowerCase();
    return CATALOGUE.map((grp) => {
      const items = grp.items.filter((it) => {
        const hay = (it.name + " " + (bg ? it.bg : it.en) + " " + (it.formula || "")).toLowerCase();
        return hay.includes(q);
      });
      return items.length ? { ...grp, items } : null;
    }).filter(Boolean);
  }, [query, bg]);

  return (
    <div
      data-testid="scientific-formulas-page"
      className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,5%)] text-slate-100" : "bg-[#FDFBF7] text-slate-900"}`}
    >
      <SEO
        title={bg ? "Научни формули и спецификация · Nutramilo" : "Scientific formulas & spec · Nutramilo"}
        description={bg ? "Пълен научен опис на всички индекси, модели и AI агенти с peer-reviewed цитати." : "Full scientific reference for every index, model and AI agent with peer-reviewed citations."}
        path="/scientific-formulas"
      />
      <SubpageNav />

      <header className={`border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "#e44288" }}>
            {bg ? "Научна спецификация" : "Scientific specification"}
          </div>
          <h1
            data-testid="formulas-page-title"
            className="text-3xl sm:text-4xl font-bold mb-3"
            style={{ fontFamily: "'Work Sans', sans-serif" }}
          >
            {bg ? "Всички формули, индекси и модели" : "All formulas, indices and models"}
          </h1>
          <p className={`text-sm sm:text-base max-w-3xl ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg
              ? "Пълен научен опис на всичко, което Nutramilo използва — с формули, единици, референтни диапазони и peer-reviewed цитати (PMID/DOI). За пациенти, лекари и журналисти, които питат: „Достоверно ли е?“."
              : "Full scientific reference for everything Nutramilo uses — formulas, units, reference ranges and peer-reviewed citations (PMID/DOI). For patients, clinicians and journalists asking: 'Is this credible?'."}
          </p>

          {/* Search */}
          <div className={`mt-6 flex items-center gap-2 rounded-xl border px-3 py-2 max-w-md ${isDark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <Search className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
            <input
              data-testid="formulas-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={bg ? "Търси формула (напр. DIL, NIS, BMI…)" : "Search formula (DIL, NIS, BMI…)"}
              className={`flex-1 bg-transparent outline-none text-sm ${isDark ? "text-slate-100" : "text-slate-900"}`}
            />
            {query && (
              <button
                data-testid="formulas-search-clear"
                onClick={() => setQuery("")}
                className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"}`}
              >
                {bg ? "Изчисти" : "Clear"}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-12">
        {filtered.length === 0 && (
          <div className={`text-sm text-center py-12 ${isDark ? "text-slate-500" : "text-slate-500"}`} data-testid="formulas-empty">
            {bg ? "Няма съвпадения." : "No matches."}
          </div>
        )}

        {filtered.map((grp, gi) => {
          const Icon = grp.icon;
          return (
            <section key={gi} data-testid={`formulas-group-${gi}`} className="scroll-mt-20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${grp.accent}22` }}>
                  <Icon className="w-5 h-5" style={{ color: grp.accent }} />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                  {bg ? grp.group.bg : grp.group.en}
                </h2>
              </div>

              <div className="space-y-4">
                {grp.items.map((it, ii) => (
                  <div
                    key={ii}
                    data-testid={`formula-card-${it.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className={`rounded-xl border p-4 sm:p-5 ${isDark ? "border-slate-800 bg-[hsl(223,47%,8%)]" : "border-slate-200 bg-white"}`}
                  >
                    <h3 className="text-base sm:text-lg font-bold mb-1">{it.name}</h3>
                    <p className={`text-xs sm:text-sm mb-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                      {bg ? it.bg : it.en}
                    </p>
                    {it.formula && (
                      <pre
                        className={`text-[11px] sm:text-xs font-mono whitespace-pre-wrap leading-relaxed rounded-lg p-3 mb-3 overflow-x-auto ${
                          isDark ? "bg-slate-950 text-emerald-300 border border-slate-800" : "bg-slate-50 text-slate-800 border border-slate-200"
                        }`}
                      >
                        {it.formula}
                      </pre>
                    )}
                    {it.refs && it.refs.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {it.refs.map((r, ri) => (
                          <a
                            key={ri}
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`formula-ref-${ri}`}
                            className={`inline-flex items-center gap-1 text-[10.5px] font-medium px-2 py-1 rounded-full border transition-colors ${
                              isDark
                                ? "border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white"
                                : "border-slate-300 text-slate-700 hover:border-slate-500 hover:text-slate-900"
                            }`}
                          >
                            <ExternalLink className="w-3 h-3" />
                            {r.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        <div className={`mt-12 rounded-xl border p-4 sm:p-5 text-xs sm:text-sm ${isDark ? "border-amber-800 bg-amber-950/30 text-amber-200" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
          <strong>{bg ? "Disclaimer:" : "Disclaimer:"}</strong>{" "}
          {bg
            ? "Nutramilo не е медицинско изделие по смисъла на MDR 2017/745. Формулите и индексите са образователни инструменти, които НЕ заместват медицинска консултация. Food Match Score (FMS) и Endo Score са собствени (peer-review pending) композитни скорове на Nutramilo — НЕ са патентовани."
            : "Nutramilo is not a medical device under MDR 2017/745. The formulas and indices are educational tools and do NOT replace medical consultation. Food Match Score (FMS) and Endo Score are our own (peer-review pending) composite scores — NOT patented."}
        </div>

        <div className="text-center pt-6">
          <Link
            to="/"
            data-testid="formulas-back-home"
            className={`inline-flex items-center gap-1 text-sm font-medium ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}
          >
            ← {bg ? "Към началната страница" : "Back to home"}
          </Link>
        </div>
      </main>

      <SubpageFooter />
    </div>
  );
}
